import { useMemo, useState } from "react";
import { NMW, TAX_YEAR, gbp } from "@/lib/statutory";

type Band = "21+" | "18-20" | "under18" | "apprentice";

export function NationalMinimumWageCalculator() {
  const [band, setBand] = useState<Band>("21+");
  const [hourlyRate, setHourlyRate] = useState(11.5);
  const [hoursPerWeek, setHoursPerWeek] = useState(37.5);

  const minRate = useMemo(() => {
    switch (band) {
      case "21+":
        return NMW.nationalLivingWage21Plus;
      case "18-20":
        return NMW.age18to20;
      case "under18":
        return NMW.under18;
      case "apprentice":
        return NMW.apprentice;
    }
  }, [band]);

  const compliant = hourlyRate >= minRate;
  const shortfallPerHour = Math.max(0, minRate - hourlyRate);
  const weeklyShortfall = shortfallPerHour * hoursPerWeek;
  const annualShortfall = weeklyShortfall * 52;

  return (
    <div className="rounded-3xl border border-border bg-card p-8 shadow-sm sm:p-10">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs font-medium text-foreground">Worker band</span>
          <select
            value={band}
            onChange={(e) => setBand(e.target.value as Band)}
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-base focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          >
            <option value="21+">21 and over (National Living Wage)</option>
            <option value="18-20">18 to 20</option>
            <option value="under18">Under 18 (above school age)</option>
            <option value="apprentice">Apprentice (under 19, or first year)</option>
          </select>
        </label>
        <NumField label="Current hourly rate (£)" value={hourlyRate} onChange={setHourlyRate} min={0} step={0.01} />
        <NumField label="Hours per week" value={hoursPerWeek} onChange={setHoursPerWeek} min={0} max={80} />
      </div>

      <div className={`mt-8 rounded-2xl p-6 ${compliant ? "bg-brand text-brand-foreground" : "bg-destructive text-destructive-foreground"}`}>
        <p className="text-xs font-semibold uppercase tracking-widest opacity-60">
          {compliant ? "Compliant" : "Underpayment"}
        </p>
        <p className="mt-3 font-serif text-5xl">
          {compliant ? gbp(hourlyRate, { decimals: 2 }) : gbp(shortfallPerHour, { decimals: 2 })}
          <span className="ml-2 text-base font-sans opacity-70">{compliant ? "/hour paid" : "/hour shortfall"}</span>
        </p>
        <p className="mt-3 text-sm opacity-80">
          Statutory minimum for this band is {gbp(minRate, { decimals: 2 })}/hour ({TAX_YEAR}).
          {!compliant && (
            <>
              {" "}
              Estimated arrears: {gbp(weeklyShortfall, { decimals: 2 })}/week, {gbp(annualShortfall, { decimals: 2 })}
              /year.
            </>
          )}
        </p>
      </div>

      <div className="mt-6 rounded-xl border border-border bg-background p-5">
        <h3 className="cursor-pointer text-sm font-medium text-brand">How this is calculated</h3>
        <div className="mt-4 space-y-3 text-sm text-muted-foreground">
          <p>
            We compare the hourly rate against the {TAX_YEAR} National Minimum / Living Wage for the worker's age band.
            Apprentices on the apprentice rate must move to the age-appropriate rate after their first year (or once
            they turn 19).
          </p>
          <p>
            HMRC can recover up to 6 years of underpayment and impose penalties of 200% of arrears, plus public naming.
          </p>
        </div>
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, min, max, step }: { label: string; value: number; onChange: (n: number) => void; min?: number; max?: number; step?: number }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-foreground">{label}</span>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-base focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
      />
    </label>
  );
}
